import { Component, inject, signal } from '@angular/core';
import {
	MAT_DIALOG_DATA,
	MatDialogActions,
	MatDialogClose,
	MatDialogContent,
	MatDialogRef,
	MatDialogTitle,
} from '@angular/material/dialog';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { LEK_PROVIDER, provideLekApi } from '../../../../../api/services/lek.service';
import { NOTIFICATION_SERVICE } from '../../../../../shared/notification/notification';
import { LvrButton } from '../../../../../core/components/lvr-button/lvr-button';
import { createErrorNotification } from '../../../../../shared/notification/notification.factory';

@Component({
	selector: 'lvr-lek-delete-dialog',
	template: `
		<h2 mat-dialog-title>{{ 'lek.delete.title' | transloco }}</h2>
		<mat-dialog-content>
			<p>
				{{ 'lek.delete.confirmation' | transloco }}
			</p>
		</mat-dialog-content>
		<mat-dialog-actions class="actions-row">
			<lvr-button
				variant="secondary"
				[label]="'base.button.cancel' | transloco"
				[type]="'button'"
				mat-dialog-close
				[icon]="'disabled_by_default'"
			></lvr-button>
			<lvr-button
				variant="primary"
				[label]="'base.button.delete' | transloco"
				[loadingLabel]="'base.button.deleting' | transloco"
				[type]="'button'"
				(clicked)="deleteLek()"
				[loading]="loading()"
				[icon]="'delete'"
			></lvr-button>
		</mat-dialog-actions>
	`,
	styles: [
		`
			.full-width {
				width: 100%;
				margin-top: 16px;
			}
		`,
	],
	imports: [MatDialogTitle, MatDialogContent, MatDialogActions, MatDialogClose, TranslocoPipe, LvrButton],
	providers: [provideLekApi],
})
export class LekDeleteDialog {
	private readonly dialogRef = inject(MatDialogRef<LekDeleteDialog>);
	private readonly data = inject(MAT_DIALOG_DATA) as {
		lekId: number | string;
		onDeleted: () => Promise<void>;
	};
	private readonly lekApi = inject(LEK_PROVIDER);
	private readonly ntfySrv = inject(NOTIFICATION_SERVICE);
	private readonly transloco = inject(TranslocoService);

	public readonly loading = signal(false);

	public async deleteLek() {
		if (this.loading()) return;

		this.loading.set(true);
		this.dialogRef.disableClose = true;

		try {
			await this.lekApi.deleteItem(this.data.lekId);
			await this.data.onDeleted();
			this.dialogRef.close(true);
		} catch {
			this.ntfySrv.addNotification(
				createErrorNotification(this.transloco.translate('base.notification_message.lek_delete_error')),
			);
		} finally {
			this.loading.set(false);
			this.dialogRef.disableClose = false;
		}
	}
}
